import {
  Button,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Grid, Space, VStack} from 'platform/foundation';
import {z} from 'zod';

import {type ReactNode} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

export interface RentalVehicleBlockFormValues {
  from: Date;
  to: Date;
  reasonId: string;
  note?: string | Nullish;
}

interface RentalVehicleBlockFormProps {
  reasonOptions: Array<{value: string; label: string}>;
  defaultValues?: RentalVehicleBlockFormValues;
  submitButtonTitle: string;
  onSubmit: FormSubmitHandler<RentalVehicleBlockFormValues>;
  onChange?: (values: RentalVehicleBlockFormValues) => void;
  beforeActions?: ReactNode;
  isSubmitDisabled?: boolean;
  isSubmitLoading?: boolean;
}

const schema = z
  .object({
    from: z.date({required_error: i18n.t('general.validations.fieldIsRequired')}),
    to: z.date({required_error: i18n.t('general.validations.fieldIsRequired')}),
    reasonId: z
      .string({required_error: i18n.t('general.validations.fieldIsRequired')})
      .min(1, i18n.t('general.validations.fieldIsRequired')),
    // A blank note is no note: the backend stores it as null, so every caller receives null, not ''
    note: z
      .string()
      .trim()
      .nullish()
      .transform((note) => (isNilOrEmpty(note) ? null : note)),
  })
  .refine((data) => data.from < data.to, {
    message: i18n.t('page.rentalOrder.errors.toBeforeFromError'),
    path: ['to'],
  });

export function RentalVehicleBlockForm(props: RentalVehicleBlockFormProps) {
  return (
    <Form<RentalVehicleBlockFormValues>
      onSubmit={props.onSubmit}
      onChange={props.onChange}
      defaultValues={props.defaultValues}
      experimentalZodSchema={schema}
    >
      {(control) => (
        <>
          <Grid columns={2}>
            <FormField
              control={control}
              name="from"
              type="dateTime"
              label={i18n.t('entity.rental.labels.from')}
              data-testid="rentalVehicleBlock-from"
            />
            <FormField
              control={control}
              name="to"
              type="dateTime"
              label={i18n.t('entity.rental.labels.to')}
              data-testid="rentalVehicleBlock-to"
            />
          </Grid>
          <Space vertical={4} />
          <FormField
            control={control}
            name="reasonId"
            type="choice"
            label={i18n.t('general.labels.reason')}
            options={props.reasonOptions}
            isRequired
            menuInPortal
            data-testid="rentalVehicleBlock-reasonId"
          />
          <Space vertical={4} />
          <FormField
            control={control}
            name="note"
            type="textarea"
            label={i18n.t('entity.rental.labels.note')}
            data-testid="rentalVehicleBlock-note"
          />
          <Space vertical={4} />
          <VStack spacing={4}>
            {props.beforeActions}
            <Grid columns={2}>
              <Button
                variant="secondary"
                onClick={closeCurrentDialog}
                title={i18n.t('general.actions.discard')}
                data-testid="rentalVehicleBlock-discard"
              />
              <FormButton
                control={control}
                type="submit"
                title={props.submitButtonTitle}
                isDisabled={props.isSubmitDisabled}
                isLoading={props.isSubmitLoading}
                data-testid="rentalVehicleBlock-submit"
              />
            </Grid>
          </VStack>
        </>
      )}
    </Form>
  );
}
